import { ReactNode, useState } from "react"
import HeadcountTable from "./headcount-table"
import TurnoverTable from "./turnover-table"
import EmployeeMovementChart from "./employee-movement-chart"
import TurnoverChart from "./turnover-chart"
import CompareTable from "./compare-table"
import CompareChart from "./compare-chart"
import GroupHeadcountTable from "./group-headcount-table"
import RetentionTables from "./retention-tables"
import GroupToggle from "./group-toggle"
import { HeadcountResult, HrGroupBy, HrView, HrWarning } from "models/hr-dashboard"
import { HrDashboardViews } from "slices/human-resources/hr-dashboard/reducer"

interface HrViewContentProps {
    view: HrView
    views: HrDashboardViews
    groupBy: HrGroupBy
    // Omitted on the print page: no department / division toggle
    setGroupBy?: (groupBy: HrGroupBy) => void
    // Print page: formula details always open, no buttons
    printMode?: boolean
}

const Warnings = ({ warnings }: { warnings: HrWarning[] }) => warnings.length === 0 ? null : (
    <div className="alert alert-warning py-2 mb-3">
        {warnings.map((w, i) => <div key={`${w.code}-${i}`}><i className="bx bx-error me-1" />{w.message}</div>)}
    </div>
)

const Empty = () => <p className="text-muted my-4">No data for this period.</p>

const hasData = (months: { values?: unknown; total?: unknown }[] | undefined) =>
    !!months && months.some(m => (m.values ?? m.total ?? null) !== null)

const Section = ({ children }: { children: ReactNode }) => <div className="mt-4">{children}</div>

// V1, V2 and V4 share the same table and chart
const headcountContent = (data: HeadcountResult | null | undefined, label: string, chartTitle: string, showAverage = false) =>
    !data || !hasData(data.months) ? <Empty /> : <>
        <Warnings warnings={data.warnings} />
        <HeadcountTable data={data} label={label} showAverage={showAverage} />
        <Section><EmployeeMovementChart data={data} title={chartTitle} /></Section>
    </>

// The content of one dashboard tab
const HrViewContent = ({ view, views, groupBy, setGroupBy, printMode = false }: HrViewContentProps) => {
    const [formulaOpen, setFormulaOpen] = useState(false)
    const toggle = setGroupBy ? <GroupToggle value={groupBy} onChange={setGroupBy} /> : null

    switch (view) {
        case "headcount": return headcountContent(views.headcount, "Employees", "Employee Movement", true)
        case "consultants": return headcountContent(views.consultants, "Consultants", "Consultant Movement")
        case "movement": return headcountContent(views.movement, "Employees + Consultants", "All Movement")

        case "by-group": {
            const data = views["by-group"]
            return <>
                {toggle}
                {!data || !hasData(data.total.months) ? <Empty /> : <GroupHeadcountTable data={data} />}
            </>
        }

        case "compare": {
            const data = views.compare
            return !data || !hasData(data.months) ? <Empty /> : <>
                <Warnings warnings={data.warnings} />
                <CompareTable data={data} />
                <Section><CompareChart data={data} /></Section>
            </>
        }

        case "turnover": {
            const data = views.turnover
            if (!data || !hasData(data.months)) return <Empty />
            const showDetails = printMode || formulaOpen
            return <>
                <Warnings warnings={data.warnings} />
                <p className="text-muted mb-2">
                    Formula: {data.formulaDescription}.{" "}
                    {!printMode && (
                        <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setFormulaOpen(!formulaOpen)}>
                            {formulaOpen ? "Hide details" : "Details"}
                        </button>
                    )}
                </p>
                {showDetails && (
                    <div className="small text-muted mb-3">
                        Month, quarter and YTD use the same formula over their own period. Regrettable and
                        non-regrettable use the same denominator. Quarter cells are red when above the
                        maximum level ({(data.quarterMax * 100).toFixed(1)}%) and green otherwise.
                        Departures with no regrettable mapping show as Unclassified.
                    </div>
                )}
                <TurnoverTable data={data} />
                <Section><TurnoverChart data={data} /></Section>
            </>
        }

        case "retention": {
            const data = views.retention
            return <>
                {toggle}
                {!data ? <Empty /> : <>
                    <Warnings warnings={data.warnings} />
                    <RetentionTables data={data} />
                </>}
            </>
        }
    }
}

export default HrViewContent
